import type { PreferencesSummary } from '../api/types';

/**
 * The two numbers this feature owns that the API does *not* supply, and the one place on the client
 * where the five defaults are allowed to appear at all.
 *
 * **The completions rule (FR-007, research R-002).** Home fetched 20 and showed 5; the containers
 * fetched 5 and showed 5. FR-007 makes them one preference, and the rule is `fetch = max(20, N)`,
 * `show = N`, with "show more" revealing the remainder. The floor is not a preference and never
 * becomes one: it exists so that "show more" has something to reveal, and so that a practitioner who
 * asks for 3 does not get a page that cannot grow. Setting `pageSize` to N instead would be wrong
 * twice over — it caps a practitioner asking for 40 at exactly 40 with nothing held back, and
 * `Paging.Normalize` on the server turns a `pageSize` of **0 into 100** (research R-009), so the
 * practitioner who asks to see no completions at all would fetch a hundred of them.
 *
 * **DEGRADED_FALLBACK is the FR-015 carve-out, and the only one.** FR-015 forbids restating the
 * defaults on the client: every value a preference governs must reach the screen from the
 * practitioner's stored value, and the Domain (`PractitionerPreferences.Defaults`) is the single
 * definition. FR-012 nonetheless requires the client to fall back to those same defaults when the
 * read fails — and a read that failed cannot deliver the values it failed to deliver. All five
 * preferences govern client-side rendering, so there is no ordering of the two requirements that
 * satisfies both; the spec was amended during implementation to permit exactly one client module to
 * state them, and this is that module. SC-014's check counts this occurrence as the permitted one
 * and fails on a second anywhere else.
 *
 * The rules that follow from that, and that the store enforces:
 *
 * - It is **never an initial value.** `PreferencesStore` holds `null` until a read settles, so
 *   nothing can paint a default and then correct itself (FR-011, SC-009) — the fallback takes effect
 *   only once the read has *definitively failed*, which is also when `degraded` goes true.
 * - It is reachable only through the same accessors a successful read feeds. No screen imports it.
 * - It is not a *reset to defaults*: FR-018 declines that control for exactly this reason, and
 *   FR-012 forbids saving at all while degraded, so these values can never be written back as though
 *   the practitioner had chosen them.
 *
 * Every value here equals what governed the screens before this feature (FR-003), including the one
 * deliberate divergence from the reference: *show completed projects in sidebar* is **false**,
 * against Tracks' `true`, because `features/home/model/sidebar.ts` filters them out today
 * (research R-005, R-007).
 */
export const COMPLETED_FETCH_FLOOR = 20;

/** How many completed actions to *fetch* for a practitioner who shows `n` of them (FR-007). */
export function completedFetchSize(n: number): number {
  return Math.max(COMPLETED_FETCH_FLOOR, n);
}

/** FR-012's fallback, used only after a preferences read has definitively failed. See above. */
export const DEGRADED_FALLBACK: PreferencesSummary = {
  completionsShown: 5,
  reviewPeriodDays: 14,
  stalenessStartsDays: 7,
  showCompletedProjectsInSidebar: false,
  showHiddenContextsInSidebar: true,
  // The sixth is `null` rather than a zone id, and that is not an omission: `null` means "never
  // chosen", which resolves through the browser's own zone (010 FR-001 rung two). Naming a zone here
  // — 'UTC' most temptingly — would state a choice the practitioner never made, and would make a
  // degraded read behave differently from a successful one that returned `null` (010 FR-002 amended,
  // research R-003).
  timeZone: null,
  // The three display preferences (015). Unlike timeZone, absence is not a value on this wire — a
  // read always answers a resolved spelling — so the fallback states the Domain defaults: the
  // exact behaviour of the application before 015 shipped (worded dates, the worded masthead, the
  // light palette).
  dateFormat: 'worded',
  titleDateFormat: 'weekday-day-month-year',
  theme: 'light',
  weekStart: 'monday',
  // 018: the notifications cadence is resolved on the wire like the display preferences, and the
  // fallback states the Domain default — never, exactly the behaviour before the digest existed.
  emailNotifications: 'never',
  // 023: `null`, which on this wire means "no explicit choice" — the same thing a successful read
  // returns for a practitioner who has never chosen. So unlike the five display fields above, this
  // fallback is not a restatement of a Domain default; it is simply the commonest stored state, and
  // it happens to be the honest answer for a read that failed too.
  //
  // Nothing depends on it. It governs the Settings picker's selection, and FR-012 already forbids
  // saving while degraded, so the worst it can do is show "My Inbox" on a screen that cannot be
  // submitted.
  captureContextId: null,
};
